import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { translationsAPI } from '../services/api';
import { getAuth, hasPermission } from '../utils/auth';
import { t } from '../utils/i18n';

function Translations() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { user } = getAuth();
    const [data, setData] = useState([]);
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [, forceUpdate] = useState(0);
    
    // Facilitator state
    const [newEntryEnglish, setNewEntryEnglish] = useState('');
    const [newEntryWesternArmenian, setNewEntryWesternArmenian] = useState('');
    const [newEntryId, setNewEntryId] = useState('');
    const [isAddingEntry, setIsAddingEntry] = useState(false);
    
    // Export selection state
    const [showExportModal, setShowExportModal] = useState(false);
    const [selectedEntries, setSelectedEntries] = useState(new Set());
    const [rangeStartId, setRangeStartId] = useState('');
    const [rangeEndId, setRangeEndId] = useState('');
    
    // Pagination state: 50 default, options 50/100/150/200
    const PAGE_SIZE_OPTIONS = [50, 100, 150, 200];
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [modalExportPage, setModalExportPage] = useState(1);
    
    // Comments state
    const [comments, setComments] = useState({});
    const [showComments, setShowComments] = useState({});
    const [newComment, setNewComment] = useState({ entryId: null, fieldName: '', text: '', isSuggestion: false });
    
    // Editing state - tracks which entries are in edit mode (forced by user)
    const [editingEntries, setEditingEntries] = useState(new Set());
    const [openMenus, setOpenMenus] = useState({});
    // Track which entries have Eastern Armenian hint in edit mode
    const [editingEasternArmenianHint, setEditingEasternArmenianHint] = useState({});
    // Combined edit: when true for an entry, both English and Eastern Armenian are editable (admin only)
    const [editingEnglishAndEastern, setEditingEnglishAndEastern] = useState({});
    
    // Debounce timers for field updates - prevents race conditions when typing fast
    const debounceTimers = useRef({});
    const pendingSaves = useRef({});
    // Ref to read Eastern Armenian textarea value on check click (state may not have updated yet)
    const easternArmenianTextareaRef = useRef({});
    // Ref to store latest Eastern Armenian value on every keystroke (backup when DOM ref is stale)
    const easternArmenianValueRef = useRef({});
    // Ref to read English textarea value when closing combined edit (so we save before closing)
    const originalEnglishTextareaRef = useRef({});
    // Ref to store latest English value on every keystroke (fallback when textarea ref is null after scroll/re-render)
    const originalEnglishValueRef = useRef({});
    // Ref to store latest field value per entry+field so Send can flush correct value (state may be stale)
    const lastFieldValueRef = useRef({});

    useEffect(() => {
        loadTranslations();
    }, [projectId]);

    // Reset to page 1 when data length or itemsPerPage changes; keep currentPage in bounds
    const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));
    const displayData = data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    useEffect(() => {
        setCurrentPage(prev => Math.min(prev, totalPages || 1));
    }, [data.length, itemsPerPage, totalPages]);

    // Avoid loading comments for the whole project at once.
    // Load only comments for visible page entries and cache them in state.
    useEffect(() => {
        if (!displayData.length) return;
        const missingCommentEntryIds = displayData
            .map((item) => item.id)
            .filter((id) => comments[id] === undefined);
        missingCommentEntryIds.forEach((id) => loadComments(id));
    }, [displayData, comments]);

    // When opening export modal, reset modal page to 1
    useEffect(() => {
        if (showExportModal) setModalExportPage(1);
    }, [showExportModal]);

    useEffect(() => {
        const handleLanguageChange = () => {
            forceUpdate(prev => prev + 1);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange);
            // Cleanup debounce timers on unmount
            Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
        };
    }, []);

    const loadTranslations = async () => {
        try {
            setLoading(true);
            setMessage('');
            console.log(`🔍 Loading translations for project ${projectId}...`);
            console.log(`👤 User role: ${user?.role}`);
            const response = await translationsAPI.getByProject(projectId);
            console.log('📥 Response:', response.data);
            if (response.data.success) {
                const entries = response.data.data || [];
                console.log(`✅ Loaded ${entries.length} entries`);
                
                // Log status breakdown for translator
                if (user?.role === 'translator') {
                    const pendingTranslation = entries.filter(e => e.status === 'pending_translation').length;
                    const pendingEditing = entries.filter(e => e.status === 'pending_editing').length;
                    console.log(`📊 Status breakdown for translator:`);
                    console.log(`   - pending_translation: ${pendingTranslation}`);
                    console.log(`   - pending_editing: ${pendingEditing}`);
                    console.log(`   - Total visible: ${pendingTranslation + pendingEditing}`);
                }
                
                // Sort entries based on role:
                // - Admin: newest first (DESC by id) - new entries at top
                // - Others: oldest first (ASC by id) - new entries at bottom
                const sortedEntries = user?.role === 'admin' 
                    ? [...entries].sort((a, b) => (b.id || 0) - (a.id || 0))
                    : [...entries].sort((a, b) => (a.id || 0) - (b.id || 0));
                
                setData(sortedEntries);
                setProject(response.data.project);
                
                if (entries.length === 0) {
                    console.log('⚠️ No entries found in response');
                }
            } else {
                console.error('❌ API returned success: false', response.data);
                setMessage(response.data.error || t('error'));
            }
        } catch (error) {
            console.error('❌ Error loading translations:', error);
            console.error('Error details:', error.response?.data);
            const errorMessage = error.response?.data?.error || error.message || t('error');
            setMessage(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    const loadComments = async (entryId) => {
        try {
            const response = await translationsAPI.getComments(entryId);
            if (response.data.success) {
                setComments(prev => ({
                    ...prev,
                    [entryId]: response.data.comments
                }));
            }
        } catch (error) {
            console.error('Error loading comments:', error);
        }
    };

    // Helper function to get final accepted word
    // Priority: confirmerText > reviewerText > editorText
    const getFinalAcceptedWord = (item) => {
        if (item.confirmerText && item.confirmerText.trim()) {
            return item.confirmerText;
        }
        if (item.reviewerText && item.reviewerText.trim()) {
            return item.reviewerText;
        }
        if (item.editorText && item.editorText.trim()) {
            return item.editorText;
        }
        return item.originalSource || '';
    };

    // Helper function to get status text
    const getStatusText = (item) => {
        if (item.confirmed || item.isConfirmed) {
            return t('confirmed');
        }
        if (item.status === 'pending_confirmation' && !item.confirmed) {
            return t('postponed');
        }
        return t('current');
    };

    // Format "Showing X–Y of Z" for pagination
    const formatShowing = (from, to, total) => {
        const s = t('showingXtoYofZ') || 'Showing {{from}}–{{to}} of {{total}}';
        return s.replace(/\{\{from\}\}/g, from).replace(/\{\{to\}\}/g, to).replace(/\{\{total\}\}/g, total);
    };

    // Helper function to check if translator can edit an entry
    // Returns true only if entry is in initial state (pending_translation or pending_editing)
    // After sending to editor, translator must use "Edit" button to enable editing
    const canTranslatorEdit = (item) => {
        if (!item) {
            console.log(`🚫 Translator cannot edit: item is null/undefined`);
            return false; // No item, cannot edit
        }
        // Check confirmed status - cannot edit confirmed entries
        const isConfirmed = item.confirmed === true || item.isConfirmed === true || item.confirmed === 1 || item.isConfirmed === 1;
        if (isConfirmed) {
            console.log(`🚫 Translator cannot edit entry ${item.id}: entry is confirmed`);
            return false; // Cannot edit confirmed entries
        }
        // Can edit ONLY if entry is in initial state (not yet sent to editor)
        // After sending (status becomes pending_review or higher), must use "Edit" button
        const canEditInitial = item.status === 'pending_translation' || item.status === 'pending_editing';
        if (canEditInitial) {
            console.log(`✅ Translator can edit entry ${item.id}: entry is in initial state (status=${item.status})`);
            return true;
        }
        // Entry has been sent, cannot edit unless "Edit" button is clicked
        console.log(`🚫 Translator cannot edit entry ${item.id}: entry has been sent (status=${item.status}), use "Edit" button`);
        return false;
    };
    
    // Helper function to check if translator can use "Edit" button
    // Returns true if entry is NOT sent to reviewer/confirmer yet (can edit while editor is reviewing)
    // Translator can edit while editor is reviewing (pending_review), but NOT after editor sends to reviewer (pending_confirmation)
    const canTranslatorUseEditButton = (item) => {
        if (!item) return false;
        // Cannot edit if confirmed
        const isConfirmed = item.confirmed === true || item.isConfirmed === true || item.confirmed === 1 || item.isConfirmed === 1;
        if (isConfirmed) return false;
        // Can use Edit button if:
        // 1. Entry is in initial state (pending_translation or pending_editing), OR
        // 2. Entry is with editor (pending_review) - editor hasn't sent it yet
        // Cannot edit if editor has already sent it (pending_confirmation or higher)
        const canEdit = item.status === 'pending_translation' || 
                       item.status === 'pending_editing' || 
                       item.status === 'pending_review';
        return canEdit;
    };

    // Helper function to check if editor can edit an entry
    const canEditorEdit = (item) => {
        // Can edit if status is pending_review (sent from translator)
        return item.status === 'pending_review';
    };

    // Helper function to check if reviewer can edit an entry
    const canReviewerEdit = (item) => {
        // Can edit if status is pending_confirmation (sent from editor)
        return item.status === 'pending_confirmation';
    };

    // Helper function to check if confirmer can edit/confirm an entry
    const canConfirmerEdit = (item) => {
        // Can edit/confirm if status is pending_confirmation (sent from reviewer)
        return item.status === 'pending_confirmation' && !item.confirmed;
    };

    // Optimistic update - update UI immediately, save to server after debounce
    const updateFieldOptimistic = (entryId, field, value) => {
        // Update local state immediately (optimistic update) - user sees typing instantly
        setData(prevData =>
            prevData.map(item =>
                item.id === entryId ? { ...item, [field]: value } : item
            )
        );
    };

    // Display ID: custom admin-editable label, or fallback to numeric id
    const getDisplayId = (item) => (item.displayId != null && String(item.displayId).trim() !== '') ? String(item.displayId).trim() : String(item.id);
    const getNumericDisplayId = (item) => {
        const parsed = Number.parseInt(getDisplayId(item), 10);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const selectEntriesByRange = () => {
        const fromValue = Number.parseInt(rangeStartId, 10);
        const toValue = Number.parseInt(rangeEndId, 10);

        if (Number.isNaN(fromValue) || Number.isNaN(toValue)) {
            setMessage(t('enterValidRange'));
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        const start = Math.min(fromValue, toValue);
        const end = Math.max(fromValue, toValue);
        const rangeIds = data
            .filter(item => {
                const numericDisplayId = getNumericDisplayId(item);
                return numericDisplayId !== null && numericDisplayId >= start && numericDisplayId <= end;
            })
            .map(item => item.id);

        if (rangeIds.length === 0) {
            setMessage(t('noEntriesInRange'));
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        setSelectedEntries(new Set(rangeIds));
    };

    // Save to server (debounced)
    const saveFieldToServer = async (entryId, field, value) => {
        const item = data.find(item => item.id === entryId);
        
        // Check if entry is confirmed (only admin can edit confirmed)
        const isConfirmed = item && (item.confirmed || item.isConfirmed);
        if (isConfirmed && user?.role !== 'admin') {
            setMessage(t('cannotEditConfirmed'));
            setTimeout(() => setMessage(''), 3000);
            return;
        }
        
        // For translator, also check if they can edit this field
        if (user?.role === 'translator' && field === 'translatorText') {
            // Translator can edit if entry is not confirmed (already checked above)
            // OR if they clicked "Edit" button (editingEntries has this entry)
            const isEditingMode = editingEntries.has(entryId);
            if (isConfirmed && !isEditingMode) {
                setMessage(t('cannotEditConfirmed'));
                setTimeout(() => setMessage(''), 3000);
                return;
            }
            // If in editing mode, allow edit even if entry was sent (but not confirmed)
            if (isEditingMode && !isConfirmed) {
                console.log(`✅ Translator editing mode: allowing edit for entry ${entryId}`);
            }
        }

        // Role-based field editing permissions
        const roleFieldMap = {
            'facilitator': ['originalEnglish', 'originalSource', 'easternArmenianHint', 'translatorText', 'editorText', 'reviewerText', 'confirmerText', 'facilitatorNotes'],
            'translator': ['translatorText'],
            'editor': ['editorText'],
            'reviewer': ['originalEnglish', 'originalSource', 'easternArmenianHint', 'translatorText', 'editorText', 'reviewerText', 'confirmerText', 'facilitatorNotes'],
            'confirmer': ['confirmerText'],
            'admin': ['originalEnglish', 'originalSource', 'easternArmenianHint', 'displayId', 'translatorText', 'editorText', 'reviewerText', 'confirmerText', 'facilitatorNotes']
        };

        const allowedFields = roleFieldMap[user?.role] || [];
        if (!allowedFields.includes(field)) {
            setMessage(t('noPermission'));
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        const saveKey = `${entryId}-${field}`;
        pendingSaves.current[saveKey] = value;

        try {
            if (field === 'easternArmenianHint') {
                console.log(`[easternArmenianHint] Saving entry ${entryId}, value length: ${typeof value === 'string' ? value.length : 0}`);
            }
            if (field === 'originalEnglish') {
                console.log(`[originalEnglish] PUT entry ${entryId}, value length: ${typeof value === 'string' ? value.length : 0}`);
            }
            const res = await translationsAPI.updateEntry(entryId, field, value != null ? String(value) : '');
            if (pendingSaves.current[saveKey] === value) {
                delete pendingSaves.current[saveKey];
            }
            if (field === 'easternArmenianHint' || field === 'originalEnglish') {
                setMessage(t('saved'));
                setTimeout(() => setMessage(''), 2000);
                // Don't refetch here — optimistic update already shows the new value; refetch caused screen flicker on every save
            }
        } catch (error) {
            delete pendingSaves.current[saveKey];
            const errMsg = error.response?.data?.error || error.message || t('error');
            if (field === 'easternArmenianHint') {
                console.error('[easternArmenianHint] Save failed:', error.response?.data || error.message);
            }
            if (field === 'originalEnglish') {
                console.error('[originalEnglish] Save failed:', error.response?.data || error.message);
            }
            setMessage(errMsg);
            setTimeout(() => setMessage(''), 5000);
        }
    };

    // Main update function with debouncing
    const updateField = (entryId, field, value) => {
        // Debug log for translator
        if (user?.role === 'translator' && field === 'translatorText') {
            const item = data.find(item => item.id === entryId);
            const editable = canTranslatorEdit(item);
            const isEditingMode = editingEntries.has(entryId);
            const canUseEditButton = canTranslatorUseEditButton(item);
            console.log(`📝 Translator trying to update entry ${entryId}: editable=${editable}, editingMode=${isEditingMode}, canUseEditButton=${canUseEditButton}, value="${value.substring(0, 30)}..."`);
            // Allow update ONLY if:
            // 1. Entry is in initial state (editable = true), OR
            // 2. Edit button was clicked AND entry is still in initial state (not sent to editor)
            if (!editable && (!isEditingMode || !canUseEditButton)) {
                console.warn(`⚠️ Translator cannot update entry ${entryId}: entry has been sent to editor or is not editable`);
                return;
            }
        }
        
        // Update UI immediately (optimistic update)
        updateFieldOptimistic(entryId, field, value);
        // Store latest value so Send flush can use it even if state hasn't updated yet
        lastFieldValueRef.current[`${entryId}-${field}`] = value;

        // Clear existing debounce timer for this field
        const timerKey = `${entryId}-${field}`;
        if (debounceTimers.current[timerKey]) {
            clearTimeout(debounceTimers.current[timerKey]);
        }
        
        // Set new debounce timer - save to server after 500ms of no typing
        debounceTimers.current[timerKey] = setTimeout(() => {
            saveFieldToServer(entryId, field, value);
            delete debounceTimers.current[timerKey];
        }, 500);
    };

    const handleSendToWorkflow = async (entryId, to) => {
        try {
            // CRITICAL: Wait for any pending saves to complete before sending
            // This ensures the latest text is saved before backend validates it
            // Map destination to required field
            const fieldMap = {
                'editor': 'translatorText',
                'reviewer': 'editorText',
                'confirmer': 'editorText',
                'translator': 'originalSource'
            };
            const requiredField = fieldMap[to];
            
            if (requiredField) {
                const pendingSaveKey = `${entryId}-${requiredField}`;
                if (pendingSaves.current[pendingSaveKey] !== undefined) {
                    console.log(`[Send] Waiting for pending save for entry ${entryId}, field: ${requiredField}...`);
                    // Wait for debounce timer to complete (500ms) + save time (max 3 seconds)
                    await new Promise(resolve => {
                        const checkPending = () => {
                            if (pendingSaves.current[pendingSaveKey] === undefined) {
                                // Save completed
                                resolve();
                            } else {
                                // Still pending, check again after a short delay
                                setTimeout(checkPending, 100);
                            }
                        };
                        // Start checking immediately
                        checkPending();
                        // Timeout after 3 seconds to prevent infinite wait
                        setTimeout(() => {
                            if (pendingSaves.current[pendingSaveKey] !== undefined) {
                                console.warn(`[Send] Timeout waiting for save, proceeding anyway for entry ${entryId}`);
                                resolve();
                            }
                        }, 3000);
                    });
                    console.log(`[Send] Pending save completed for entry ${entryId}`);
                }
                
                // Also ensure any debounce timer for this entry is executed immediately
                const timerKey = `${entryId}-${requiredField}`;
                if (debounceTimers.current[timerKey]) {
                    console.log(`[Send] Flushing debounce timer for entry ${entryId}, field: ${requiredField}`);
                    clearTimeout(debounceTimers.current[timerKey]);
                    // Use ref first (latest typed value); state may not have updated yet if user sent right after typing
                    const refValue = lastFieldValueRef.current[timerKey];
                    const item = data.find(item => item.id === entryId);
                    const valueToSave = refValue !== undefined ? refValue : (item && item[requiredField]);
                    if (valueToSave !== undefined) {
                        await saveFieldToServer(entryId, requiredField, valueToSave);
                    }
                    delete lastFieldValueRef.current[timerKey];
                    delete debounceTimers.current[timerKey];
                }
            }
            
            const response = await translationsAPI.sendToWorkflow(entryId, to);
            if (response.data.success) {
                loadTranslations();
                setMessage(t('sentSuccessfully'));
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (error) {
            setMessage(error.response?.data?.error || t('error'));
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handleAddNew = async () => {
        if (isAddingEntry) {
            return;
        }
        if (!newEntryEnglish.trim()) {
            setMessage(t('englishRequired'));
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        try {
            setIsAddingEntry(true);
            console.log('🔍 Creating new entry:', { projectId, id: newEntryId, originalEnglish: newEntryEnglish, originalSource: newEntryWesternArmenian });
            const response = await translationsAPI.createEntry(projectId, {
                id: newEntryId || undefined,
                originalEnglish: newEntryEnglish,
                originalSource: newEntryWesternArmenian
            });
            console.log('✅ Create entry response:', response.data);

            if (response.data.success) {
                setNewEntryEnglish('');
                setNewEntryWesternArmenian('');
                setNewEntryId('');
                // Reload translations - new entry will appear at top for admin, bottom for others
                await loadTranslations();
                setMessage(t('saved'));
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (error) {
            console.error('❌ Error adding new entry:', error);
            console.error('Error response:', error.response);
            console.error('Error response data:', error.response?.data);
            console.error('Error response status:', error.response?.status);
            console.error('Error message:', error.message);
            
            let errorMessage = t('error');
            if (error.response?.data) {
                if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                } else if (error.response.data.errors && error.response.data.errors.length > 0) {
                    errorMessage = error.response.data.errors[0].msg || error.response.data.errors[0].message || t('error');
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            console.error('Setting error message:', errorMessage);
            setMessage(errorMessage);
            setTimeout(() => setMessage(''), 5000);
        } finally {
            setIsAddingEntry(false);
        }
    };

    const handleConfirm = async (entryId) => {
        try {
            const response = await translationsAPI.confirmEntry(entryId, true);
            if (response.data.success) {
                loadTranslations();
                setMessage(t('confirmed'));
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (error) {
            setMessage(error.response?.data?.error || t('error'));
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handlePostpone = async (entryId) => {
        try {
            const response = await translationsAPI.confirmEntry(entryId, false);
            if (response.data.success) {
                loadTranslations();
                setMessage(t('postpone') + ' - ' + t('saved'));
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (error) {
            setMessage(error.response?.data?.error || t('error'));
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm(t('confirmDeleteEntry'))) {
            return;
        }

        try {
            const response = await translationsAPI.deleteEntry(entryId);
            if (response.data.success) {
                loadTranslations();
                setMessage(t('entryDeleted'));
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (error) {
            setMessage(error.response?.data?.error || t('error'));
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handleAddComment = async (entryId, fieldName) => {
        // BUG FIX: Ստուգում ենք, արդյոք newComment state-ը համընկնում է սեղմված կոճակի entryId-ի և fieldName-ի հետ
        // Եթե չեն համընկնում, բայց կա մուտքագրված տեքստ, ապա օգտատիրոջը պետք է նախ պահպանի կամ մաքրի նախորդ մեկնաբանությունը
        // Սա կանխում է սխալ մեկնաբանությունների ավելացումը սխալ entry-ին կամ field-ին
        const commentText = newComment.entryId === entryId && newComment.fieldName === fieldName ? newComment.text : '';
        
        // Եթե կա մուտքագրված տեքստ, բայց entryId-ն կամ fieldName-ը չեն համընկնում, ցույց տալ հաղորդագրություն
        if (!commentText.trim() && newComment.text && newComment.text.trim() && 
            (newComment.entryId !== entryId || newComment.fieldName !== fieldName)) {
            setMessage(t('commentRequired') + ' - ' + t('pleaseSaveOrClearPreviousComment'));
            setTimeout(() => setMessage(''), 3000);
            return;
        }
        
        if (!commentText.trim()) {
            setMessage(t('commentRequired'));
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        try {
            const response = await translationsAPI.addComment(
                entryId, 
                fieldName, 
                commentText, 
                false // Always false, no suggestions
            );
            
            if (response.data.success) {
                setNewComment({ entryId: null, fieldName: '', text: '', isSuggestion: false });
                loadComments(entryId);
                setMessage(t('saved'));
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (error) {
            setMessage(error.response?.data?.error || t('error'));
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const renderFacilitatorView = () => {
        // Facilitator: Read-only view - can only view, cannot edit, add, or send
        return (
            <div className="translation-list">
                <h3 className="table-title" style={{ marginBottom: '16px' }}>
                    {t('translations')} ({data.length})
                </h3>
                {displayData.length > 0 ? (
                    displayData.map((item) => {
                        const isConfirmed = item.confirmed || item.isConfirmed;
                        const isPostponed = item.status === 'pending_confirmation' && !isConfirmed;
                        const finalAcceptedWord = isConfirmed ? getFinalAcceptedWord(item) : '';
                        const shouldShowField = isConfirmed || isPostponed;
                        return (
                        <div key={item.id} className="translation-entry-card" style={{
                    background: 'var(--bg-color)', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    border: '1px solid var(--border-color)',
                            marginBottom: '16px'
                }}>
                            <div style={{ display: 'grid', gridTemplateColumns: shouldShowField ? '1fr 1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr 1fr', gap: '16px' }}>
                        <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('translationText')}
                                    </label>
                            <textarea
                                className="form-input"
                                        value={item.originalEnglish || ''}
                                        disabled
                                rows={5}
                                        style={{ background: 'white', color: 'var(--brand-primary)', fontWeight: 'bold', resize: 'both' }}
                            />
                                    {/* Show Eastern Armenian hint - always show if exists, read-only, preserves formatting */}
                                    {(item.easternArmenianHint ?? item.originalSource) && (item.easternArmenianHint ?? item.originalSource).trim() && (
                                        <div style={{ marginTop: '8px', padding: '10px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '13px' }}>
                                            <div style={{ fontWeight: '600', marginBottom: '4px', color: '#1976d2' }}>
                                                {t('easternArmenian')}՝
                                            </div>
                                            <div style={{ 
                                                color: '#424242', 
                                                whiteSpace: 'pre-wrap', 
                                                wordBreak: 'break-word',
                                                lineHeight: '1.5'
                                            }}>
                                                {item.easternArmenianHint ?? item.originalSource}
                                            </div>
                                        </div>
                                    )}
                        </div>
                        <div>
                                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontWeight: '600' }}>
                                        <span>{t('translation')}</span>
                                        <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>ID: {getDisplayId(item)}</span>
                                    </label>
                            <textarea
                                className="form-input"
                                        value={item.translatorText || ''}
                                        disabled
                                rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                            />
                        </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('editing')}
                                    </label>
                                    <textarea
                                        className="form-input armenian-text"
                                        value={item.editorText || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                    />
                    </div>
                                    <div>
                                        <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('review')}
                                        </label>
                                        <textarea
                                            className="form-input armenian-text"
                                        value={item.reviewerText || ''}
                                            disabled
                                            rows={5}
                                            style={{ background: '#f5f5f5', resize: 'both' }}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('confirmation')}
                                        </label>
                                        <textarea
                                            className="form-input armenian-text"
                                        value={item.confirmerText || ''}
                                        disabled
                                            rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                        />
                                    </div>
                                    {shouldShowField && (
                                        <div>
                                            <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: isPostponed ? '#d32f2f' : '#2e7d32' }}>
                                                {t('westernArmenian')}
                                            </label>
                                            <textarea
                                                className="form-input armenian-text"
                                                value={isPostponed ? t('postponed') : finalAcceptedWord}
                                                disabled
                                                rows={5}
                                                style={{ 
                                                    background: isPostponed ? '#ffebee' : '#e8f5e9',
                                                    color: isPostponed ? '#d32f2f' : '#2e7d32',
                                                    fontWeight: '600',
                                                    border: isPostponed ? '2px solid #d32f2f' : '2px solid #4caf50',
                                                    resize: 'both'
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            <div style={{ marginTop: '12px', padding: '8px', background: '#e9ecef', borderRadius: '4px', fontSize: '12px' }}>
                                <strong>{getStatusText(item)}</strong>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="empty-state">
                        <div className="empty-icon">📝</div>
                        <h3 className="empty-title">{t('noTranslationsYet')}</h3>
                        <p className="empty-description">{t('noTranslationsInProject')}</p>
                    </div>
                )}
            </div>
        );
    };

    const renderTranslatorView = () => {
        // Translator sees all entries, but can only edit pending ones
        const canEdit = (item) => canTranslatorEdit(item);
        const pendingCount = data.filter(item => canEdit(item)).length;
        
        return (
            <div className="translation-list">
                <h3 className="table-title" style={{ marginBottom: '16px' }}>
                    {t('translations')} ({data.length}) - {t('pendingTranslation')}: {pendingCount}
                </h3>
                {displayData.map((item) => {
                    const editable = canEdit(item);
                    const isConfirmed = item.confirmed || item.isConfirmed;
                    // Debug log for translator editing
                    if (item.id) {
                        console.log(`🔍 Translator View - Entry ${item.id}: editable=${editable}, confirmed=${isConfirmed}, status=${item.status}`);
                    }
                    const isPostponed = item.status === 'pending_confirmation' && !isConfirmed;
                    const finalAcceptedWord = isConfirmed ? getFinalAcceptedWord(item) : '';
                    const shouldShowField = isConfirmed || isPostponed;
                    return (
                    <div key={item.id} className="translation-entry-card" style={{
                        background: 'var(--bg-color)',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                            marginBottom: '16px',
                            opacity: editable ? 1 : 0.85
                    }}>
                            <div style={{ display: 'grid', gridTemplateColumns: shouldShowField ? '1fr 1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                            <div>
                                <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('translationText')}
                                </label>
                                <textarea
                                    className="form-input"
                                        value={item.originalEnglish || ''}
                                    disabled
                                    rows={5}
                                    style={{ background: 'white', color: 'var(--brand-primary)', fontWeight: 'bold', resize: 'both' }}
                                />
                                    {/* Show Eastern Armenian hint - always show if exists, read-only, preserves formatting */}
                                    {(item.easternArmenianHint ?? item.originalSource) && (item.easternArmenianHint ?? item.originalSource).trim() && (
                                        <div style={{ marginTop: '8px', padding: '10px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '13px' }}>
                                            <div style={{ fontWeight: '600', marginBottom: '4px', color: '#1976d2' }}>
                                                {t('easternArmenian')}՝
                                            </div>
                                            <div style={{ 
                                                color: '#424242', 
                                                whiteSpace: 'pre-wrap', 
                                                wordBreak: 'break-word',
                                                lineHeight: '1.5'
                                            }}>
                                                {item.easternArmenianHint ?? item.originalSource}
                                            </div>
                                        </div>
                                    )}
                            </div>
                            <div style={{ position: 'relative' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', margin: 0 }}>
                                        <span>{t('translation')}</span>
                                        <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>ID: {getDisplayId(item)}</span>
                                    </label>
                                    {/* Three dots menu */}
                                    <div style={{ position: 'relative' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenMenus(prev => ({
                                                    ...prev,
                                                    [item.id]: !prev[item.id]
                                                }));
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px 8px',
                                                fontSize: '20px',
                                                color: '#333',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: '32px',
                                                minHeight: '32px',
                                                borderRadius: '4px',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                            title={t('options') || 'Options'}
                                        >
                                            <i className="fas fa-ellipsis-v" style={{ display: 'block' }}></i>
                                        </button>
                                        {/* Dropdown menu - always show, even when entry is disabled */}
                                        {openMenus[item.id] && (
                                            <>
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        right: 0,
                                                        background: 'white',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                        zIndex: 1000,
                                                        minWidth: '150px',
                                                        marginTop: '4px'
                                                    }}
                                                >
                                                    {/* Edit button - show only if entry is not confirmed AND not sent to editor */}
                                                    {!isConfirmed && canTranslatorUseEditButton(item) && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingEntries(prev => {
                                                                    const newSet = new Set(prev);
                                                                    if (newSet.has(item.id)) {
                                                                        newSet.delete(item.id);
                                                                    } else {
                                                                        newSet.add(item.id);
                                                                    }
                                                                    return newSet;
                                                                });
                                                                setOpenMenus(prev => ({
                                                                    ...prev,
                                                                    [item.id]: false
                                                                }));
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 16px',
                                                                textAlign: 'left',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                fontSize: '14px',
                                                                color: '#333',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                                                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                        >
                                                            <i className={editingEntries.has(item.id) ? "fas fa-check" : "fas fa-edit"}></i>
                                                            <span>{editingEntries.has(item.id) ? t('confirm') : t('edit')}</span>
                                                        </button>
                                                    )}
                                                    {/* Show message if entry has been sent to reviewer/confirmer (editor already sent it) */}
                                                    {!isConfirmed && !canTranslatorUseEditButton(item) && item.status !== 'pending_translation' && item.status !== 'pending_editing' && item.status !== 'pending_review' && (
                                                        <div style={{
                                                            padding: '10px 16px',
                                                            fontSize: '12px',
                                                            color: '#999',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            {t('entrySentToReviewer') || 'Entry has been sent to reviewer'}
                                                        </div>
                                                    )}
                                                    {/* Show message if confirmed */}
                                                    {isConfirmed && (
                                                        <div style={{
                                                            padding: '10px 16px',
                                                            fontSize: '12px',
                                                            color: '#999',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            {t('cannotEditConfirmed')}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Overlay to close menu when clicking outside */}
                                                <div
                                                    onClick={() => setOpenMenus(prev => ({
                                                        ...prev,
                                                        [item.id]: false
                                                    }))}
                                                    style={{
                                                        position: 'fixed',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        zIndex: 999
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                                <textarea
                                    className="form-input armenian-text"
                                        value={item.translatorText || ''}
                                        onChange={(e) => {
                                            const isEditable = editable || editingEntries.has(item.id);
                                            if (isEditable) {
                                                updateField(item.id, 'translatorText', e.target.value);
                                            }
                                        }}
                                        disabled={!editable && !editingEntries.has(item.id)}
                                        placeholder={(editable || editingEntries.has(item.id)) ? t('enterTranslation') : ''}
                                    rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: (editable || editingEntries.has(item.id)) ? 'white' : '#f5f5f5',
                                            cursor: (editable || editingEntries.has(item.id)) ? 'text' : 'not-allowed'
                                        }}
                                />
                                    {/* Comments for translatorText - show in light yellow for all */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'translatorText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                            </div>
                                    ))}
                                    {/* Add comment for translatorText - only for translator role */}
                                    {user?.role === 'translator' && (
                                        <div style={{ marginTop: '8px' }}>
                                            <textarea
                                                className="form-input"
                                                value={newComment.entryId === item.id && newComment.fieldName === 'translatorText' ? newComment.text : ''}
                                                onChange={(e) => setNewComment({ 
                                                    entryId: item.id, 
                                                    fieldName: 'translatorText', 
                                                    text: e.target.value, 
                                                    isSuggestion: false 
                                                })}
                                                placeholder={t('addComment')}
                                                rows={2}
                                                style={{ fontSize: '12px' }}
                                            />
                                            <button 
                                                onClick={() => handleAddComment(item.id, 'translatorText')}
                                                className="btn btn-outline btn-sm"
                                                style={{ marginTop: '4px' }}
                                                disabled={!(newComment.entryId === item.id && newComment.fieldName === 'translatorText' && newComment.text && newComment.text.trim())}
                                            >
                                                {t('addComment')}
                                            </button>
                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('editing')}
                                    </label>
                                    <textarea
                                        className="form-input armenian-text"
                                        value={item.editorText || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                    />
                                    {/* Comments for editorText - show in light yellow for all */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'editorText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('review')} / {t('confirmation')}
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.reviewerText || item.confirmerText || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                    />
                                    {/* Comments for reviewerText and confirmerText - show in light yellow for all */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'reviewerText' || c.fieldName === 'confirmerText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                        </div>
                                    ))}
                                </div>
                                {shouldShowField && (
                                    <div>
                                        <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: isPostponed ? '#d32f2f' : '#2e7d32' }}>
                                            {t('westernArmenian')}
                                        </label>
                                        <textarea
                                            className="form-input armenian-text"
                                            value={isPostponed ? t('postponed') : finalAcceptedWord}
                                            disabled
                                            rows={5}
                                            style={{ 
                                                background: isPostponed ? '#ffebee' : '#e8f5e9',
                                                color: isPostponed ? '#d32f2f' : '#2e7d32',
                                                fontWeight: '600',
                                                border: isPostponed ? '2px solid #d32f2f' : '2px solid #4caf50',
                                                resize: 'both'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    <strong>{getStatusText(item)}</strong>
                                </div>
                                {editable && (
                                    <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                onClick={() => handleSendToWorkflow(item.id, 'editor')}
                                className="btn btn-primary"
                                disabled={!item.translatorText || item.translatorText.trim() === ''}
                            >
                                <i className="fas fa-paper-plane"></i>
                                <span>{t('sendToEditor')}</span>
                            </button>
                        </div>
                                )}
                    </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderEditorView = () => {
        // Editor sees all entries, but can only edit pending ones
        const canEdit = (item) => canEditorEdit(item);
        const pendingCount = data.filter(item => canEdit(item)).length;
        
        return (
            <div className="translation-list">
                <h3 className="table-title" style={{ marginBottom: '16px' }}>
                    {t('translations')} ({data.length}) - {t('pendingReview')}: {pendingCount}
                </h3>
                {displayData.map((item) => {
                    const editable = canEdit(item);
                    const isConfirmed = item.confirmed || item.isConfirmed;
                    const isPostponed = item.status === 'pending_confirmation' && !isConfirmed;
                    const finalAcceptedWord = isConfirmed ? getFinalAcceptedWord(item) : '';
                    const shouldShowField = isConfirmed || isPostponed;
                    return (
                    <div key={item.id} className="translation-entry-card" style={{
                        background: 'var(--bg-color)',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                            marginBottom: '16px',
                            opacity: editable ? 1 : 0.85
                    }}>
                            <div style={{ display: 'grid', gridTemplateColumns: shouldShowField ? '1fr 1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                            <div>
                                <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('translationText')}
                                </label>
                                <textarea
                                    className="form-input"
                                        value={item.originalEnglish || ''}
                                    disabled
                                    rows={5}
                                    style={{ background: 'white', color: 'var(--brand-primary)', fontWeight: 'bold', resize: 'both' }}
                                />
                                    {/* Show Eastern Armenian hint - always show if exists, read-only, preserves formatting */}
                                    {(item.easternArmenianHint ?? item.originalSource) && (item.easternArmenianHint ?? item.originalSource).trim() && (
                                        <div style={{ marginTop: '8px', padding: '10px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '13px' }}>
                                            <div style={{ fontWeight: '600', marginBottom: '4px', color: '#1976d2' }}>
                                                {t('easternArmenian')}՝
                                            </div>
                                            <div style={{ 
                                                color: '#424242', 
                                                whiteSpace: 'pre-wrap', 
                                                wordBreak: 'break-word',
                                                lineHeight: '1.5'
                                            }}>
                                                {item.easternArmenianHint ?? item.originalSource}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontWeight: '600' }}>
                                        <span>{t('translation')}</span>
                                        <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>ID: {getDisplayId(item)}</span>
                                    </label>
                                    <textarea
                                        className="form-input armenian-text"
                                        value={item.translatorText || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                    />
                                    {/* Comments for translatorText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'translatorText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                            </div>
                                    ))}
                        </div>
                                <div style={{ position: 'relative' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <label className="form-label" style={{ display: 'block', fontWeight: '600', margin: 0 }}>
                                            {t('editing')}
                                        </label>
                                        {/* Three dots menu */}
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenus(prev => ({
                                                        ...prev,
                                                        [`editor_${item.id}`]: !prev[`editor_${item.id}`]
                                                    }));
                                                }}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px 8px',
                                                    fontSize: '18px',
                                                    color: '#666',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                                title={t('options') || 'Options'}
                                            >
                                                <i className="fas fa-ellipsis-v"></i>
                                            </button>
                                            {/* Dropdown menu */}
                                            {openMenus[`editor_${item.id}`] && (
                                                <>
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            right: 0,
                                                            background: 'white',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                            zIndex: 1000,
                                                            minWidth: '120px',
                                                            marginTop: '4px'
                                                        }}
                                                    >
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingEntries(prev => {
                                                                    const newSet = new Set(prev);
                                                                    const key = `editor_${item.id}`;
                                                                    if (newSet.has(key)) {
                                                                        newSet.delete(key);
                                                                    } else {
                                                                        newSet.add(key);
                                                                    }
                                                                    return newSet;
                                                                });
                                                                setOpenMenus(prev => ({
                                                                    ...prev,
                                                                    [`editor_${item.id}`]: false
                                                                }));
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 16px',
                                                                textAlign: 'left',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                fontSize: '14px',
                                                                color: '#333',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                                                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                        >
                                                            <i className={editingEntries.has(`editor_${item.id}`) ? "fas fa-check" : "fas fa-edit"}></i>
                                                            <span>{editingEntries.has(`editor_${item.id}`) ? t('confirm') : t('edit')}</span>
                                                        </button>
                                                    </div>
                                                    {/* Overlay to close menu when clicking outside */}
                                                    <div
                                                        onClick={() => setOpenMenus(prev => ({
                                                            ...prev,
                                                            [`editor_${item.id}`]: false
                                                        }))}
                                                        style={{
                                                            position: 'fixed',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            zIndex: 999
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <textarea
                                        className="form-input armenian-text"
                                        value={item.editorText || ''}
                                        onChange={(e) => {
                                            const isEditable = editable || editingEntries.has(`editor_${item.id}`);
                                            if (isEditable) {
                                                updateField(item.id, 'editorText', e.target.value);
                                            }
                                        }}
                                        disabled={!editable && !editingEntries.has(`editor_${item.id}`)}
                                        placeholder={(editable || editingEntries.has(`editor_${item.id}`)) ? t('enterEditing') : ''}
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: (editable || editingEntries.has(`editor_${item.id}`)) ? 'white' : '#f5f5f5',
                                            cursor: (editable || editingEntries.has(`editor_${item.id}`)) ? 'text' : 'not-allowed'
                                        }}
                                    />
                                    {/* Comments for editorText - show in light yellow for all */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'editorText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                        </div>
                                    ))}
                                    {/* Add comment for editorText - only for editor role */}
                                    {user?.role === 'editor' && (
                                        <div style={{ marginTop: '8px' }}>
                                            <textarea
                                                className="form-input"
                                                value={newComment.entryId === item.id && newComment.fieldName === 'editorText' ? newComment.text : ''}
                                                onChange={(e) => setNewComment({ 
                                                    entryId: item.id, 
                                                    fieldName: 'editorText', 
                                                    text: e.target.value, 
                                                    isSuggestion: false 
                                                })}
                                                placeholder={t('addComment')}
                                                rows={2}
                                                style={{ fontSize: '12px' }}
                                            />
                                            <button 
                                                onClick={() => handleAddComment(item.id, 'editorText')}
                                                className="btn btn-outline btn-sm"
                                                style={{ marginTop: '4px' }}
                                                disabled={!(newComment.entryId === item.id && newComment.fieldName === 'editorText' && newComment.text && newComment.text.trim())}
                                            >
                                                {t('addComment')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('review')} / {t('confirmation')}
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.reviewerText || item.confirmerText || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                    />
                                    {/* Comments for reviewerText and confirmerText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'reviewerText' || c.fieldName === 'confirmerText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                        </div>
                                    ))}
                                </div>
                                {shouldShowField && (
                                    <div>
                                        <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: isPostponed ? '#d32f2f' : '#2e7d32' }}>
                                            {t('westernArmenian')}
                                        </label>
                                        <textarea
                                            className="form-input armenian-text"
                                            value={isPostponed ? t('postponed') : finalAcceptedWord}
                                            disabled
                                            rows={5}
                                            style={{ 
                                                background: isPostponed ? '#ffebee' : '#e8f5e9',
                                                color: isPostponed ? '#d32f2f' : '#2e7d32',
                                                fontWeight: '600',
                                                border: isPostponed ? '2px solid #d32f2f' : '2px solid #4caf50',
                                                resize: 'both'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    <strong>{getStatusText(item)}</strong>
                                </div>
                                {editable && (
                                    <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                onClick={() => handleSendToWorkflow(item.id, 'reviewer')}
                                className="btn btn-primary"
                                disabled={!item.editorText || item.editorText.trim() === ''}
                            >
                                <i className="fas fa-paper-plane"></i>
                                <span>{t('sendToReviewer')}</span>
                            </button>
                        </div>
                                )}
                    </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderReviewerView = () => {
        // Reviewer: Admin-like - can edit all fields, but only when status is pending_confirmation
        const canEdit = (item) => canReviewerEdit(item);
        const pendingCount = data.filter(item => canEdit(item)).length;
        
        return (
            <div className="translation-list">
                <h3 className="table-title" style={{ marginBottom: '16px' }}>
                    {t('translations')} ({data.length}) - {t('pendingConfirmation')}: {pendingCount}
                </h3>
                {displayData.map((item) => {
                    const editable = canEdit(item);
                    const isConfirmed = item.confirmed || item.isConfirmed;
                    const isPostponed = item.status === 'pending_confirmation' && !isConfirmed;
                    const finalAcceptedWord = isConfirmed ? getFinalAcceptedWord(item) : '';
                    const shouldShowField = isConfirmed || isPostponed;
                    return (
                        <div key={item.id} className="translation-entry-card" style={{
                            background: 'var(--bg-color)',
                            padding: '20px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            marginBottom: '16px',
                            opacity: editable ? 1 : 0.85
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: shouldShowField ? '1fr 1fr 1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('translationText')}
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.originalEnglish || ''}
                                        onChange={(e) => editable && updateField(item.id, 'originalEnglish', e.target.value)}
                                        disabled={!editable}
                                        placeholder={editable ? t('enterEnglishText') : ''}
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: 'white',
                                            color: editable ? 'inherit' : 'var(--brand-primary)',
                                            fontWeight: editable ? 'normal' : 'bold'
                                        }}
                                    />
                                    {/* Show Eastern Armenian hint if admin provided it */}
                                    {(item.easternArmenianHint ?? item.originalSource) && (item.easternArmenianHint ?? item.originalSource).trim() && (
                                        <div style={{ marginTop: '8px', padding: '10px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '13px' }}>
                                            <div style={{ fontWeight: '600', marginBottom: '4px', color: '#1976d2' }}>
                                                {t('easternArmenian')}՝
                                            </div>
                                            <div style={{ 
                                                color: '#424242', 
                                                whiteSpace: 'pre-wrap', 
                                                wordBreak: 'break-word',
                                                lineHeight: '1.5'
                                            }}>
                                                {item.easternArmenianHint ?? item.originalSource}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontWeight: '600' }}>
                                        <span>{t('translation')}</span>
                                        <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>ID: {getDisplayId(item)}</span>
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.translatorText || ''}
                                        onChange={(e) => editable && updateField(item.id, 'translatorText', e.target.value)}
                                        disabled={!editable}
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: editable ? 'white' : '#f5f5f5'
                                        }}
                                    />
                                    {/* Comments for translatorText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'translatorText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                                </div>
                                            ))}
                                        </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('editing')}
                                    </label>
                                    <textarea
                                        className="form-input armenian-text"
                                        value={item.editorText || ''}
                                        onChange={(e) => editable && updateField(item.id, 'editorText', e.target.value)}
                                        disabled={!editable}
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: editable ? 'white' : '#f5f5f5'
                                        }}
                                    />
                                    {/* Comments for editorText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'editorText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                </div>
                                    ))}
                            </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('review')}
                                    </label>
                                <textarea
                                    className="form-input"
                                        value={item.reviewerText || ''}
                                        onChange={(e) => editable && updateField(item.id, 'reviewerText', e.target.value)}
                                        disabled={!editable}
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: editable ? 'white' : '#f5f5f5'
                                        }}
                                    />
                                    {/* Comments for reviewerText - show in light yellow for all */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'reviewerText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                        </div>
                                    ))}
                                    {/* Add comment for reviewerText - only for reviewer role */}
                                    {user?.role === 'reviewer' && (
                                        <div style={{ marginTop: '8px' }}>
                                            <textarea
                                                className="form-input"
                                                value={newComment.entryId === item.id && newComment.fieldName === 'reviewerText' ? newComment.text : ''}
                                    onChange={(e) => setNewComment({ 
                                        entryId: item.id, 
                                                    fieldName: 'reviewerText', 
                                        text: e.target.value, 
                                                    isSuggestion: false 
                                    })}
                                                placeholder={t('addComment')}
                                    rows={2}
                                                style={{ fontSize: '12px' }}
                                            />
                                    <button 
                                                onClick={() => handleAddComment(item.id, 'reviewerText')}
                                        className="btn btn-outline btn-sm"
                                                style={{ marginTop: '4px' }}
                                                disabled={!(newComment.entryId === item.id && newComment.fieldName === 'reviewerText' && newComment.text && newComment.text.trim())}
                                    >
                                        {t('addComment')}
                                    </button>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('confirmation')}
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.confirmerText || ''}
                                        onChange={(e) => editable && updateField(item.id, 'confirmerText', e.target.value)}
                                        disabled={!editable}
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: editable ? 'white' : '#f5f5f5'
                                        }}
                                    />
                                    {/* Comments for confirmerText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'confirmerText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                        </div>
                                    ))}
                                </div>
                                {shouldShowField && (
                                    <div>
                                        <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: isPostponed ? '#d32f2f' : '#2e7d32' }}>
                                            {t('westernArmenian')}
                                        </label>
                                        <textarea
                                            className="form-input armenian-text"
                                            value={isPostponed ? t('postponed') : finalAcceptedWord}
                                            disabled
                                            rows={5}
                                            style={{ 
                                                background: isPostponed ? '#ffebee' : '#e8f5e9',
                                                color: isPostponed ? '#d32f2f' : '#2e7d32',
                                                fontWeight: '600',
                                                border: isPostponed ? '2px solid #d32f2f' : '2px solid #4caf50',
                                                resize: 'both'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    <strong>{getStatusText(item)}</strong>
                                </div>
                                {editable && (
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                <button 
                                    onClick={() => handleSendToWorkflow(item.id, 'confirmer')}
                                    className="btn btn-primary"
                                >
                                    <i className="fas fa-paper-plane"></i>
                                    <span>{t('sendToConfirmer')}</span>
                                </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderConfirmerView = () => {
        // Confirmer sees all entries, but can only edit/confirm pending ones
        const canEdit = (item) => canConfirmerEdit(item);
        const pendingCount = data.filter(item => canEdit(item)).length;
        
        return (
            <div className="translation-list">
                <h3 className="table-title" style={{ marginBottom: '16px' }}>
                    {t('translations')} ({data.length}) - {t('pendingConfirmation')}: {pendingCount}
                </h3>
                {displayData.map((item) => {
                    const editable = canEdit(item);
                    const finalAcceptedWord = getFinalAcceptedWord(item);
                    const isConfirmed = item.confirmed || item.isConfirmed;
                    
                    return (
                        <div key={item.id} className="translation-entry-card" style={{
                            background: 'var(--bg-color)',
                            padding: '20px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            marginBottom: '16px',
                            opacity: editable ? 1 : 0.85
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('translationText')}
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.originalEnglish || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: 'white', color: 'var(--brand-primary)', fontWeight: 'bold', resize: 'both' }}
                                    />
                                    {/* Show Eastern Armenian hint if admin provided it */}
                                    {(item.easternArmenianHint ?? item.originalSource) && (item.easternArmenianHint ?? item.originalSource).trim() && (
                                        <div style={{ marginTop: '8px', padding: '10px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '13px' }}>
                                            <div style={{ fontWeight: '600', marginBottom: '4px', color: '#1976d2' }}>
                                                {t('easternArmenian')}՝
                                            </div>
                                            <div style={{ 
                                                color: '#424242', 
                                                whiteSpace: 'pre-wrap', 
                                                wordBreak: 'break-word',
                                                lineHeight: '1.5'
                                            }}>
                                                {item.easternArmenianHint ?? item.originalSource}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('westernArmenian')}
                                    </label>
                                    <textarea
                                        className="form-input armenian-text"
                                        value={isConfirmed ? finalAcceptedWord : ''}
                                        disabled
                                        rows={5}
                                        style={{ background: 'white', color: 'var(--brand-secondary)', fontWeight: 'bold', resize: 'both' }}
                                    />
                                </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                                <div>
                                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontWeight: '600' }}>
                                        <span>{t('translation')}</span>
                                        <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>ID: {getDisplayId(item)}</span>
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.translatorText || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                    />
                                    {/* Comments for translatorText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'translatorText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                                </div>
                                            ))}
                                        </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('editing')}
                                    </label>
                                    <textarea
                                        className="form-input armenian-text"
                                        value={item.editorText || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                    />
                                    {/* Comments for editorText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'editorText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                </div>
                                    ))}
                            </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('review')}
                                    </label>
                                <textarea
                                    className="form-input"
                                        value={item.reviewerText || ''}
                                        disabled
                                        rows={5}
                                        style={{ background: '#f5f5f5', resize: 'both' }}
                                    />
                                    {/* Comments for reviewerText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'reviewerText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                        </div>
                                    ))}
                                    {/* Comments for confirmerText - show in light yellow */}
                                    {comments[item.id]?.filter(c => c.fieldName === 'confirmerText').map(c => (
                                        <div key={c.id} style={{ marginTop: '8px', padding: '8px', background: '#fff9c4', borderRadius: '4px', fontSize: '12px' }}>
                                            {c.commentText || c.comment_text}
                                        </div>
                                    ))}
                                    {/* Add comment for confirmerText - only for confirmer role */}
                                    {user?.role === 'confirmer' && (
                                        <div style={{ marginTop: '8px' }}>
                                            <textarea
                                                className="form-input"
                                                value={newComment.entryId === item.id && newComment.fieldName === 'confirmerText' ? newComment.text : ''}
                                    onChange={(e) => setNewComment({ 
                                        entryId: item.id, 
                                                    fieldName: 'confirmerText', 
                                        text: e.target.value, 
                                                    isSuggestion: false 
                                    })}
                                                placeholder={t('addComment')}
                                    rows={2}
                                                style={{ fontSize: '12px' }}
                                            />
                                    <button 
                                                onClick={() => handleAddComment(item.id, 'confirmerText')}
                                        className="btn btn-outline btn-sm"
                                                style={{ marginTop: '4px' }}
                                                disabled={!(newComment.entryId === item.id && newComment.fieldName === 'confirmerText' && newComment.text && newComment.text.trim())}
                                    >
                                        {t('addComment')}
                                    </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    <strong>{getStatusText(item)}</strong>
                                </div>
                                {editable && (
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                <button 
                                    onClick={() => handleConfirm(item.id)}
                                    className="btn btn-primary"
                                >
                                    <i className="fas fa-check"></i>
                                    <span>{t('confirm')}</span>
                                </button>
                                        <button 
                                            onClick={() => handlePostpone(item.id)}
                                            className="btn btn-outline"
                                        >
                                            <i className="fas fa-clock"></i>
                                            <span>{t('postpone')}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderAdminView = () => {
        // Admin sees all entries with all fields editable
        return (
            <>
                {/* Add new entry form */}
                <div style={{ 
                    background: 'var(--bg-color)', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    border: '1px solid var(--border-color)',
                    marginBottom: '24px'
                }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
                        {t('addNewTranslation')}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                        <div>
                            <label className="form-label">ID</label>
                            <input
                                type="text"
                                className="form-input"
                                value={newEntryId}
                                onChange={(e) => setNewEntryId(e.target.value)}
                                placeholder="ID"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <label className="form-label">{t('originalEnglish')} *</label>
                            <textarea
                                className="form-input"
                                value={newEntryEnglish}
                                onChange={(e) => setNewEntryEnglish(e.target.value)}
                                placeholder={t('enterEnglishText')}
                                rows={5}
                                maxLength={2500}
                                style={{ resize: 'both' }}
                            />
                        </div>
                        <div>
                            <label className="form-label">{t('easternArmenian')}</label>
                            <textarea
                                className="form-input"
                                value={newEntryWesternArmenian}
                                onChange={(e) => setNewEntryWesternArmenian(e.target.value)}
                                placeholder={t('enterEasternArmenian')}
                                rows={5}
                                style={{ resize: 'both' }}
                            />
                        </div>
                    </div>
                    <button onClick={handleAddNew} className="btn btn-primary" disabled={isAddingEntry}>
                        <i className="fas fa-plus"></i>
                        <span>{isAddingEntry ? 'Պահպանվում է...' : t('addNew')}</span>
                    </button>
                </div>

            <div className="translation-list">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 className="table-title" style={{ margin: 0 }}>
                        {t('allTranslations')} ({data.length})
                    </h3>
                    <button 
                        onClick={() => {
                            // Default: select all confirmed entries
                            const confirmedIds = new Set(data.filter(item => item.confirmed || item.isConfirmed).map(item => item.id));
                            setSelectedEntries(confirmedIds);
                            setShowExportModal(true);
                        }}
                        className="btn btn-primary"
                    >
                        <i className="fas fa-download"></i>
                        <span>{t('export')}</span>
                    </button>
                </div>
                {displayData.map((item) => {
                    const isConfirmed = item.confirmed || item.isConfirmed;
                    const finalAcceptedWord = getFinalAcceptedWord(item);
                    return (
                    <div key={item.id} className="translation-entry-card" style={{
                        background: 'var(--bg-color)',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        marginBottom: '16px',
                            opacity: isConfirmed ? 0.8 : 1
                    }}>
                        {/* ID display for admin */}
                        <div style={{ 
                            marginBottom: '12px', 
                            padding: '8px 12px', 
                            background: '#f5f5f5', 
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span style={{ fontWeight: '600', color: '#666', fontSize: '14px' }}>ID:</span>
                            {user?.role === 'admin' ? (
                                <input
                                    type="text"
                                    value={item.displayId ?? ''}
                                    onChange={(e) => updateField(item.id, 'displayId', e.target.value)}
                                    onBlur={(e) => saveFieldToServer(item.id, 'displayId', e.target.value)}
                                    placeholder={String(item.id)}
                                    style={{ fontWeight: '700', color: '#333', fontSize: '16px', width: '80px', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                    title={t('edit')}
                                />
                            ) : (
                                <span style={{ fontWeight: '700', color: '#333', fontSize: '16px' }}>{getDisplayId(item)}</span>
                            )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '12px' }}>
                            <div>
                                <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontWeight: '600' }}>
                                    <span>{t('translationText')}</span>
                                    {(user?.role === 'admin' || !isConfirmed) && (
                                        <button
                                            onClick={() => {
                                                const isCombined = editingEnglishAndEastern[item.id];
                                                if (isCombined) {
                                                    // Closing combined edit: save English immediately (debounce may not have fired)
                                                    const timerKey = `${item.id}-originalEnglish`;
                                                    if (debounceTimers.current[timerKey]) {
                                                        clearTimeout(debounceTimers.current[timerKey]);
                                                        delete debounceTimers.current[timerKey];
                                                    }
                                                    const el = originalEnglishTextareaRef.current[item.id];
                                                    const currentEnglish = (el && el.value !== undefined)
                                                        ? el.value
                                                        : (originalEnglishValueRef.current[item.id] ?? item.originalEnglish ?? '');
                                                    updateFieldOptimistic(item.id, 'originalEnglish', currentEnglish);
                                                    saveFieldToServer(item.id, 'originalEnglish', currentEnglish);
                                                }
                                                setEditingEnglishAndEastern(prev => {
                                                    const next = { ...prev };
                                                    if (isCombined) delete next[item.id];
                                                    else next[item.id] = true;
                                                    return next;
                                                });
                                                if (!isCombined) {
                                                    setEditingEasternArmenianHint(prev => ({ ...prev, [item.id]: true }));
                                                } else {
                                                    setEditingEasternArmenianHint(prev => {
                                                        const next = { ...prev };
                                                        delete next[item.id];
                                                        return next;
                                                    });
                                                }
                                            }}
                                            className="btn btn-outline btn-sm"
                                            style={{ fontSize: '11px', padding: '2px 8px' }}
                                            title={t('edit')}
                                        >
                                            <i className={`fas fa-${editingEnglishAndEastern[item.id] ? 'check' : 'edit'}`}></i>
                                        </button>
                                    )}
                                </label>
                                {editingEnglishAndEastern[item.id] && !isConfirmed ? (
                                    <textarea
                                        ref={el => { if (el) originalEnglishTextareaRef.current[item.id] = el; }}
                                        className="form-input"
                                        value={item.originalEnglish}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            originalEnglishValueRef.current[item.id] = v;
                                            updateField(item.id, 'originalEnglish', v);
                                        }}
                                        onBlur={(e) => {
                                            const v = e.target.value;
                                            originalEnglishValueRef.current[item.id] = v;
                                            const timerKey = `${item.id}-originalEnglish`;
                                            if (debounceTimers.current[timerKey]) {
                                                clearTimeout(debounceTimers.current[timerKey]);
                                                delete debounceTimers.current[timerKey];
                                            }
                                            saveFieldToServer(item.id, 'originalEnglish', v);
                                        }}
                                        rows={5}
                                        style={{ resize: 'both', background: 'white', fontFamily: 'inherit' }}
                                        autoFocus
                                    />
                                ) : (
                                    <textarea
                                        className="form-input"
                                        value={item.originalEnglish}
                                        disabled
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: 'white',
                                            color: isConfirmed ? 'var(--brand-primary)' : 'inherit',
                                            fontWeight: isConfirmed ? 'bold' : 'normal'
                                        }}
                                    />
                                )}
                                    {/* Eastern Armenian hint - always show in admin so they can add/edit; stored in easternArmenianHint (never overwritten by workflow) */}
                                    <div style={{ marginTop: '8px', padding: '10px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '13px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <div style={{ fontWeight: '600', color: '#1976d2' }}>
                                                {t('easternArmenian')}՝
                                            </div>
                                            {(user?.role === 'admin' || !isConfirmed) && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (editingEasternArmenianHint[item.id]) {
                                                            const timerKey = `${item.id}-easternArmenianHint`;
                                                            if (debounceTimers.current[timerKey]) {
                                                                clearTimeout(debounceTimers.current[timerKey]);
                                                                delete debounceTimers.current[timerKey];
                                                            }
                                                            const el = easternArmenianTextareaRef.current[item.id];
                                                            const currentValue = (el && el.value !== undefined) ? el.value : (item.easternArmenianHint ?? item.originalSource ?? '');
                                                            updateFieldOptimistic(item.id, 'easternArmenianHint', currentValue);
                                                            saveFieldToServer(item.id, 'easternArmenianHint', currentValue);
                                                        }
                                                        setEditingEasternArmenianHint(prev => ({
                                                            ...prev,
                                                            [item.id]: !prev[item.id]
                                                        }));
                                                    }}
                                                    className="btn btn-outline btn-sm"
                                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                                    title={t('edit')}
                                                >
                                                    <i className={`fas fa-${editingEasternArmenianHint[item.id] ? 'check' : 'edit'}`}></i>
                                                </button>
                                            )}
                                        </div>
                                        {(editingEasternArmenianHint[item.id] && (user?.role === 'admin' || !isConfirmed)) ? (
                                            <div>
                                                <textarea
                                                    ref={el => { if (el) easternArmenianTextareaRef.current[item.id] = el; }}
                                                    className="form-input"
                                                    value={item.easternArmenianHint ?? item.originalSource ?? ''}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        easternArmenianValueRef.current[item.id] = v;
                                                        updateField(item.id, 'easternArmenianHint', v);
                                                    }}
                                                    placeholder={t('enterEasternArmenian')}
                                                    rows={3}
                                                    style={{ 
                                                        width: '100%',
                                                        fontSize: '13px',
                                                        padding: '8px',
                                                        border: '1px solid #90caf9',
                                                        borderRadius: '4px',
                                                        fontFamily: 'inherit'
                                                    }}
                                                    autoFocus
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ 
                                                color: '#424242', 
                                                whiteSpace: 'pre-wrap', 
                                                wordBreak: 'break-word',
                                                lineHeight: '1.5',
                                                minHeight: '20px'
                                            }}>
                                                {(item.easternArmenianHint ?? item.originalSource) || <span style={{ color: '#90a4ae', fontStyle: 'italic' }}>—</span>}
                                            </div>
                                        )}
                                    </div>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                            <div>
                                <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontWeight: '600' }}>
                                        <span>{t('translation')}</span>
                                        <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>ID: {getDisplayId(item)}</span>
                                </label>
                                <textarea
                                    className="form-input"
                                    value={item.translatorText}
                                        onChange={(e) => !isConfirmed && updateField(item.id, 'translatorText', e.target.value)}
                                        disabled={isConfirmed}
                                    rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: isConfirmed ? '#f5f5f5' : 'white'
                                        }}
                                />
                            </div>
                            <div>
                                <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('editing')}
                                </label>
                                <textarea
                                    className="form-input"
                                    value={item.editorText}
                                        onChange={(e) => !isConfirmed && updateField(item.id, 'editorText', e.target.value)}
                                        disabled={isConfirmed}
                                    rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: isConfirmed ? '#f5f5f5' : 'white'
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('review')}
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.reviewerText || ''}
                                        onChange={(e) => !isConfirmed && updateField(item.id, 'reviewerText', e.target.value)}
                                        disabled={isConfirmed}
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: isConfirmed ? '#f5f5f5' : 'white'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('facilitatorNotes')}
                                    </label>
                                    <textarea
                                        className="form-input"
                                        value={item.facilitatorNotes || ''}
                                        onChange={(e) => !isConfirmed && updateField(item.id, 'facilitatorNotes', e.target.value)}
                                        disabled={isConfirmed}
                                        rows={5}
                                        style={{ 
                                            resize: 'both',
                                            background: isConfirmed ? '#f5f5f5' : 'white'
                                        }}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '12px' }}>
                            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                                {t('status')}: {item.status}
                            </span>
                                {isConfirmed && (
                                <span style={{ fontSize: '14px', color: 'var(--brand-secondary)', fontWeight: '600' }}>
                                    ✓ {t('confirmed')}
                                </span>
                            )}
                                {!isConfirmed && (
                                    <button 
                                        onClick={() => handleConfirm(item.id)}
                                        className="btn btn-primary"
                                    >
                                        <i className="fas fa-check"></i>
                                        <span>{t('confirm')}</span>
                                    </button>
                                )}
                                <button 
                                    onClick={() => handleDeleteEntry(item.id)}
                                    className="btn btn-outline"
                                    style={{ color: '#dc3545', borderColor: '#dc3545' }}
                                    title={t('deleteEntry')}
                                >
                                    <i className="fas fa-trash"></i>
                                    <span>{t('delete')}</span>
                                </button>
                        </div>
                    </div>
                    );
                })}
            </div>
            </>
        );
    };

    if (loading) {
        return (
            <Layout>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div className="loading" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
                </div>
            </Layout>
        );
    }

    const confirmedCount = data.filter(item => item.confirmed).length;

    return (
        <Layout>
            <div className="content-header">
                <button 
                    onClick={() => navigate('/projects')}
                    className="btn btn-outline"
                    style={{ marginBottom: '16px' }}
                >
                    ← {t('back')}
                </button>
                <h1 className="page-title">{project?.name || t('translations')}</h1>
                <p className="page-description">
                    {t('confirmed')}: {confirmedCount} / {data.length}
                </p>
            </div>

            {message && (
                <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
                    {message}
                </div>
            )}

            {/* Role-based view */}
            {user?.role === 'facilitator' && renderFacilitatorView()}
            {user?.role === 'translator' && renderTranslatorView()}
            {user?.role === 'editor' && renderEditorView()}
            {user?.role === 'reviewer' && renderReviewerView()}
            {user?.role === 'confirmer' && renderConfirmerView()}
            {user?.role === 'admin' && renderAdminView()}

            {/* Pagination bar: Showing X–Y of Z, per-page 50/100/150/200, Prev, 1 2 3…, Next */}
            {data.length > 0 && user && (
                <div style={{
                    marginTop: '24px',
                    padding: '16px',
                    background: 'var(--bg-secondary, #f8f9fa)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #dee2e6)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 500, color: 'var(--text-secondary, #6c757d)' }}>
                            {formatShowing(
                                data.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1,
                                Math.min(currentPage * itemsPerPage, data.length),
                                data.length
                            )}
                        </span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                            <span style={{ color: 'var(--text-secondary, #6c757d)' }}>{t('perPage')}:</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                style={{
                                    padding: '6px 10px',
                                    borderRadius: '4px',
                                    border: '1px solid var(--border-color, #ced4da)',
                                    background: 'white',
                                    fontSize: '14px'
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage <= 1}
                            className="btn btn-outline btn-sm"
                            style={{ minWidth: '36px' }}
                            title={t('previous')}
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => {
                                if (totalPages <= 7) return true;
                                if (p === 1 || p === totalPages) return true;
                                if (Math.abs(p - currentPage) <= 2) return true;
                                return false;
                            })
                            .reduce((acc, p, idx, arr) => {
                                const prev = arr[idx - 1];
                                if (prev !== undefined && p - prev > 1) acc.push('…');
                                acc.push(p);
                                return acc;
                            }, [])
                            .map((p, idx) =>
                                p === '…' ? (
                                    <span key={`ellipsis-${idx}`} style={{ padding: '0 4px' }}>…</span>
                                ) : (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => setCurrentPage(p)}
                                        className={currentPage === p ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                                        style={{ minWidth: '36px' }}
                                    >
                                        {p}
                                    </button>
                                )
                            )}
                        <button
                            type="button"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage >= totalPages}
                            className="btn btn-outline btn-sm"
                            style={{ minWidth: '36px' }}
                            title={t('next')}
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}

            {/* Export Modal: paginated list; export includes all selected from every page */}
            {showExportModal && user?.role === 'admin' && (() => {
                const totalModalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));
                const displayDataModal = data.slice((modalExportPage - 1) * itemsPerPage, modalExportPage * itemsPerPage);
                return (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        maxWidth: '800px',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        width: '90%'
                    }}>
                        <h3 style={{ marginBottom: '16px' }}>{t('selectForExport')}</h3>
                        <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            <button 
                                onClick={() => {
                                    const confirmedIds = new Set(data.filter(item => item.confirmed || item.isConfirmed).map(item => item.id));
                                    setSelectedEntries(confirmedIds);
                                }}
                                className="btn btn-outline btn-sm"
                            >
                                {t('selectConfirmed')}
                            </button>
                            <button 
                                onClick={() => setSelectedEntries(new Set(data.map(item => item.id)))}
                                className="btn btn-outline btn-sm"
                            >
                                {t('selectAll')}
                            </button>
                            <button 
                                onClick={() => setSelectedEntries(new Set())}
                                className="btn btn-outline btn-sm"
                            >
                                {t('deselectAll')}
                            </button>
                        </div>
                        <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            <input
                                type="number"
                                value={rangeStartId}
                                onChange={(e) => setRangeStartId(e.target.value)}
                                placeholder={t('rangeFromId')}
                                className="form-control"
                                style={{ width: '130px' }}
                            />
                            <input
                                type="number"
                                value={rangeEndId}
                                onChange={(e) => setRangeEndId(e.target.value)}
                                placeholder={t('rangeToId')}
                                className="form-control"
                                style={{ width: '130px' }}
                            />
                            <button
                                type="button"
                                onClick={selectEntriesByRange}
                                className="btn btn-outline btn-sm"
                            >
                                {t('selectByRange')}
                            </button>
                        </div>
                        <div style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>
                            {formatShowing(
                                data.length === 0 ? 0 : (modalExportPage - 1) * itemsPerPage + 1,
                                Math.min(modalExportPage * itemsPerPage, data.length),
                                data.length
                            )} • {t('selectedCount')}: {selectedEntries.size}
                        </div>
                        <div style={{ maxHeight: '320px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                            {displayDataModal.map((item) => {
                                const isSelected = selectedEntries.has(item.id);
                                const isConfirmed = item.confirmed || item.isConfirmed;
                                return (
                                    <div key={item.id} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        padding: '8px',
                                        background: isSelected ? '#e8f5e9' : 'transparent',
                                        borderBottom: '1px solid #eee'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                const newSelected = new Set(selectedEntries);
                                                if (e.target.checked) {
                                                    newSelected.add(item.id);
                                                } else {
                                                    newSelected.delete(item.id);
                                                }
                                                setSelectedEntries(newSelected);
                                            }}
                                            style={{ marginRight: '12px' }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                                ID: {getDisplayId(item)} {isConfirmed && <span style={{ color: '#4caf50' }}>✓ {t('confirmed')}</span>}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                {item.originalEnglish?.substring(0, 100)}...
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                onClick={() => setModalExportPage(p => Math.max(1, p - 1))}
                                disabled={modalExportPage <= 1}
                                className="btn btn-outline btn-sm"
                                style={{ minWidth: '36px' }}
                                title={t('previous')}
                            >
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            {Array.from({ length: totalModalPages }, (_, i) => i + 1)
                                .filter(p => totalModalPages <= 7 || p === 1 || p === totalModalPages || Math.abs(p - modalExportPage) <= 2)
                                .reduce((acc, p, idx, arr) => {
                                    const prev = arr[idx - 1];
                                    if (prev !== undefined && p - prev > 1) acc.push('…');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === '…' ? (
                                        <span key={`modal-ellipsis-${idx}`} style={{ padding: '0 4px' }}>…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            type="button"
                                            onClick={() => setModalExportPage(p)}
                                            className={modalExportPage === p ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                                            style={{ minWidth: '36px' }}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}
                            <button
                                type="button"
                                onClick={() => setModalExportPage(p => Math.min(totalModalPages, p + 1))}
                                disabled={modalExportPage >= totalModalPages}
                                className="btn btn-outline btn-sm"
                                style={{ minWidth: '36px' }}
                                title={t('next')}
                            >
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>
                        <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => setShowExportModal(false)}
                                className="btn btn-outline"
                            >
                                {t('cancel')}
                            </button>
                            <button 
                                onClick={async () => {
                                    if (selectedEntries.size === 0) {
                                        setMessage(t('selectAtLeastOne'));
                                        setTimeout(() => setMessage(''), 3000);
                                        return;
                                    }
                                    try {
                                        const selectedIds = Array.from(selectedEntries);
                                        const response = await translationsAPI.exportProject(projectId, selectedIds);
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', `${project?.name || 'translations'}.xlsx`);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        setShowExportModal(false);
                                        setMessage(t('exportSuccess'));
                                        setTimeout(() => setMessage(''), 3000);
                                    } catch (error) {
                                        setMessage(error.response?.data?.error || t('exportError'));
                                        setTimeout(() => setMessage(''), 3000);
                                    }
                                }}
                                className="btn btn-primary"
                                disabled={selectedEntries.size === 0}
                            >
                                <i className="fas fa-download"></i>
                                <span>{t('exportSelected')} ({selectedEntries.size})</span>
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Empty state - only show if no entries at all */}
            {data.length === 0 && user?.role !== 'admin' && (
                <div className="empty-state">
                    <div className="empty-icon">📝</div>
                    <h3 className="empty-title">{t('noTranslationsYet')}</h3>
                    <p className="empty-description">{t('startByAdding')}</p>
                </div>
            )}
        </Layout>
    );
}

export default Translations;
